<template>
  <div class="calendario-actividades">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Calendario de Actividades</h1>
        <p class="content-header-subtitle">Visualiza las actividades en formato calendario</p>
      </div>
      <div class="content-header-right">
        <b-button variant="primary" @click="nuevaActividad">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          Nueva Actividad
        </b-button>
      </div>
    </div>

    <!-- Filtros -->
    <b-card class="mb-3">
      <b-row>
        <b-col md="3">
          <b-form-group label="Buscar">
            <b-input-group>
              <b-form-input
                v-model="filtros.busqueda"
                placeholder="Buscar actividades..."
                @input="aplicarFiltros"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary">
                  <feather-icon icon="SearchIcon" size="16" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Estado">
            <b-form-select
              v-model="filtros.estado"
              :options="opcionesEstado"
              @change="aplicarFiltros"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Proyecto">
            <b-input-group>
              <b-form-input
                v-model="filtros.proyecto"
                placeholder="Buscar por proyecto..."
                @keyup.enter="aplicarFiltros"
              />
              <b-input-group-append>
                <b-button variant="outline-primary" @click="aplicarFiltros">
                  <feather-icon icon="SearchIcon" size="16" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-button variant="outline-secondary" @click="limpiarFiltros">
            Limpiar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Calendario -->
    <b-card>
      <div class="calendar-container">
        <FullCalendar
          ref="fullCalendar"
          :options="calendarOptions"
          class="calendar"
        />
      </div>
    </b-card>

    <!-- Leyenda de Colores -->
    <b-card class="mt-3" v-if="estadosActividades.length > 0">
      <h6 class="mb-3">Leyenda de Estados</h6>
      <div class="legend-container">
        <div 
          v-for="estado in estadosActividades" 
          :key="estado.idEstadoActividad"
          class="legend-item"
        >
          <div 
            class="legend-color" 
            :style="{ backgroundColor: getColorPorEstado(estado.nombre) }"
          ></div>
          <span class="legend-text">{{ estado.nombre }}</span>
        </div>
        <div class="legend-item">
          <div class="legend-color" style="background-color: #6c757d;"></div>
          <span class="legend-text">Sin Estado</span>
        </div>
      </div>
      
      <!-- Leyenda de Tipos de Eventos -->
      <div class="mt-4">
        <h6 class="mb-3">Tipos de Eventos</h6>
        <div class="legend-container">
          <div class="legend-item">
            <div class="legend-color" style="background-color: #007bff;"></div>
            <span class="legend-text">📅 Fecha de Creación</span>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background-color: #0056b3;"></div>
            <span class="legend-text">⏰ Próxima Acción</span>
          </div>
        </div>
        <small class="text-muted">
          Los eventos de próxima acción aparecen con colores más oscuros para distinguirlos de las fechas de creación.
        </small>
      </div>
    </b-card>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { ActividadService } from '@/services/actividadService'
import { EstadoActividadService } from '@/services/estadoActividadService'
import { Actividad, EstadoActividad } from '@/types'

export default {
  name: 'CalendarioActividades',
  components: {
    FullCalendar,
  },
  data() {
    return {
      cargando: false,
      actividades: [],
      estadosActividades: [],
      filtros: {
        busqueda: '',
        estado: '',
        proyecto: '',
      },
      opcionesEstado: [
        { value: '', text: 'Todos los estados' },
      ],
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: 'es',
        firstDay: 1, // Lunes como primer día de la semana
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        },
        buttonText: {
          today: 'Hoy',
          month: 'Mes',
          week: 'Semana',
          day: 'Día'
        },
        events: [],
        eventClick: this.onEventClick,
        eventMouseEnter: this.onEventMouseEnter,
        eventMouseLeave: this.onEventMouseLeave,
        height: 'auto',
        dayMaxEvents: true,
        moreLinkClick: 'popover',
        eventDisplay: 'block',
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        },
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }
      }
    }
  },
  async mounted() {
    await this.cargarEstadosActividades()
    await this.cargarActividades()
  },
  methods: {
    async cargarEstadosActividades() {
      try {
        this.estadosActividades = await EstadoActividadService.getAll()
        this.opcionesEstado = [
          { value: '', text: 'Todos los estados' },
          ...this.estadosActividades.map(estado => ({
            value: estado.nombre,
            text: estado.nombre
          }))
        ]
      } catch (error) {
        console.error('Error al cargar estados de actividades:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar los estados de actividades</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      }
    },
    async cargarActividades() {
      this.cargando = true
      try {
        this.actividades = await ActividadService.getAll()
        this.actualizarEventosCalendario()
      } catch (error) {
        console.error('Error al cargar actividades:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar las actividades</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      } finally {
        this.cargando = false
      }
    },
    actualizarEventosCalendario() {
      const eventos = []
      
      this.actividadesFiltradas.forEach(actividad => {
        // Evento para la fecha de creación de la actividad
        eventos.push({
          id: `${actividad.idActividad}-creacion`,
          title: `📅 ${actividad.tipoActividad?.nombre || 'Sin tipo'} - ${actividad.nombreProyecto || 'Sin proyecto'}`,
          start: actividad.fecha,
          allDay: true,
          backgroundColor: this.getColorPorEstado(actividad.estadoActividad?.nombre),
          borderColor: this.getColorPorEstado(actividad.estadoActividad?.nombre),
          textColor: '#ffffff',
          extendedProps: {
            actividad: actividad,
            estado: actividad.estadoActividad?.nombre,
            proyecto: actividad.nombreProyecto,
            observaciones: actividad.observaciones,
            creadoPor: actividad.creadoPor?.nombre,
            tipoEvento: 'creacion'
          }
        })
        
        // Evento para la próxima acción (solo si es diferente a la fecha de creación)
        if (actividad.proximaAccionFecha && actividad.proximaAccionFecha !== actividad.fecha) {
          eventos.push({
            id: `${actividad.idActividad}-proxima-accion`,
            title: `⏰ Próxima: ${actividad.tipoActividad?.nombre || 'Sin tipo'} - ${actividad.nombreProyecto || 'Sin proyecto'}`,
            start: actividad.proximaAccionFecha,
            allDay: true,
            backgroundColor: this.getColorPorProximaAccion(actividad.estadoActividad?.nombre),
            borderColor: this.getColorPorProximaAccion(actividad.estadoActividad?.nombre),
            textColor: '#ffffff',
            extendedProps: {
              actividad: actividad,
              estado: actividad.estadoActividad?.nombre,
              proyecto: actividad.nombreProyecto,
              observaciones: actividad.observaciones,
              creadoPor: actividad.creadoPor?.nombre,
              tipoEvento: 'proxima-accion'
            }
          })
        }
      })
      
      this.calendarOptions.events = eventos
    },
    getColorPorEstado(estado) {
      // Colores predefinidos para estados comunes
      const coloresPredefinidos = {
        'Pendiente': '#ffc107',
        'En Progreso': '#007bff',
        'Completada': '#28a745',
        'Cancelada': '#dc3545',
        'Finalizada': '#28a745',
        'En Curso': '#007bff',
        'Pausada': '#6c757d',
        'Rechazada': '#dc3545',
        'En Negociación': '#007bff',
        'En negociación': '#007bff',
      }
      
      // Si el estado tiene un color predefinido, lo usamos
      if (coloresPredefinidos[estado]) {
        return coloresPredefinidos[estado]
      }
      
      // Si no, generamos un color basado en el hash del nombre del estado
      return this.generarColorPorTexto(estado)
    },
    getColorPorProximaAccion(estado) {
      // Usar los mismos colores que la fecha de creación
      return this.getColorPorEstado(estado)
    },
    generarColorPorTexto(texto) {
      if (!texto) return '#6c757d'
      
      // Generar un hash simple del texto
      let hash = 0
      for (let i = 0; i < texto.length; i++) {
        hash = texto.charCodeAt(i) + ((hash << 5) - hash)
      }
      
      // Convertir el hash a un color HSL
      const hue = Math.abs(hash) % 360
      const saturation = 70 + (Math.abs(hash) % 30) // Entre 70% y 100%
      const lightness = 45 + (Math.abs(hash) % 20) // Entre 45% y 65%
      
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`
    },
    oscurecerColor(color, factor) {
      // Convierte el color a RGB y lo oscurece
      const hex = color.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      
      const newR = Math.floor(r * (1 - factor))
      const newG = Math.floor(g * (1 - factor))
      const newB = Math.floor(b * (1 - factor))
      
      return `rgb(${newR}, ${newG}, ${newB})`
    },
    onEventClick(info) {
      const actividad = info.event.extendedProps.actividad
      this.$router.push({ 
        name: 'actividades-detalle', 
        params: { id: actividad.idActividad } 
      })
    },
    onEventMouseEnter(info) {
      // Mostrar tooltip con información adicional
      const actividad = info.event.extendedProps.actividad
      const tooltip = `
        <div class="calendar-tooltip">
          <strong>${actividad.tipoActividad?.nombre || 'Sin tipo'}</strong><br>
          <small><strong>Proyecto:</strong> ${actividad.nombreProyecto || 'Sin proyecto'}</small><br>
          <small><strong>Estado:</strong> ${actividad.estadoActividad?.nombre}</small><br>
          <small><strong>Creado por:</strong> ${actividad.creadoPor?.nombre || 'N/A'}</small>
        </div>
      `
      // Aquí podrías implementar un tooltip personalizado si lo deseas
    },
    onEventMouseLeave(info) {
      // Ocultar tooltip
    },
    aplicarFiltros() {
      this.actualizarEventosCalendario()
    },
    limpiarFiltros() {
      this.filtros = {
        busqueda: '',
        estado: '',
        proyecto: '',
      }
      this.aplicarFiltros()
    },
    nuevaActividad() {
      this.$router.push({ name: 'actividades-nueva' })
    },
    getEstadoVariant(estado) {
      const variants = {
        'En Progreso': 'primary',
        'Completada': 'success',
        'Pendiente': 'warning',
        'Cancelada': 'danger',
      }
      return variants[estado] || 'secondary'
    },
    formatearFecha(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES')
    }
  },
  computed: {
    actividadesFiltradas() {
      let actividades = this.actividades

      if (this.filtros.busqueda) {
        const busqueda = this.filtros.busqueda.toLowerCase()
        actividades = actividades.filter(actividad =>
          actividad.tipoActividad?.nombre?.toLowerCase().includes(busqueda) ||
          actividad.nombreProyecto?.toLowerCase().includes(busqueda) ||
          actividad.creadoPor?.nombre?.toLowerCase().includes(busqueda) ||
          (actividad.observaciones && actividad.observaciones.toLowerCase().includes(busqueda))
        )
      }

      if (this.filtros.estado) {
        actividades = actividades.filter(actividad => actividad.estadoActividad?.nombre === this.filtros.estado)
      }

      if (this.filtros.proyecto) {
        const proyecto = this.filtros.proyecto.toLowerCase()
        actividades = actividades.filter(actividad => 
          actividad.nombreProyecto?.toLowerCase().includes(proyecto)
        )
      }

      return actividades
    }
  }
}
</script>

<style lang="scss" scoped>
.calendario-actividades {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.calendar-container {
  min-height: 600px;
}

.calendar {
  width: 100%;
}

// Estilos para la leyenda
.legend-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.legend-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
}

// Estilos para el calendario
:deep(.fc) {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  
  .fc-toolbar {
    margin-bottom: 1.5rem;
  }
  
  .fc-toolbar-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .fc-button {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
    font-weight: 500;
    
    &:hover {
      background-color: #0056b3;
      border-color: #0056b3;
    }
    
    &.fc-button-active {
      background-color: #0056b3;
      border-color: #0056b3;
    }
  }
  
  .fc-daygrid-event {
    border-radius: 4px;
    font-size: 0.85rem;
    padding: 2px 4px;
  }
  
  .fc-event-title {
    font-weight: 500;
  }
  
  .fc-daygrid-day-number {
    font-weight: 500;
    color: #495057;
  }
  
  .fc-daygrid-day:hover {
    background-color: #f8f9fa;
  }
  
  .fc-day-today {
    background-color: #e3f2fd !important;
  }
  
  .fc-daygrid-day-events {
    margin-top: 2px;
  }
  
  .fc-daygrid-day {
    background-color: #ffffff;
    border: 1px solid #e9ecef;
  }
  
  .fc-daygrid-day:hover {
    background-color: #f8f9fa;
  }
  
  .fc-day-today {
    background-color: #e3f2fd !important;
    border: 2px solid #2196f3 !important;
  }
  
  .fc-daygrid-day-number {
    font-weight: 500;
    color: #495057;
    padding: 8px;
  }
  
  .fc-col-header-cell {
    background-color: #f1f3f4;
    font-weight: 600;
    color: #495057;
    padding: 12px 8px;
  }
}

// Tooltip personalizado
.calendar-tooltip {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  max-width: 200px;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
  
  .calendar-container {
    min-height: 500px;
  }
  
  .legend-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  :deep(.fc) {
    .fc-toolbar {
      flex-direction: column;
      gap: 1rem;
    }
    
    .fc-toolbar-chunk {
      display: flex;
      justify-content: center;
    }
  }
}
</style>
